import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon from "../AppIcon";
import { useWriteAccess, RESTRICTED_WRITE_REASON_ID, type WriteArea } from "../../hooks/useWriteAccess";

/**
 * 🔴 CO-24 — the INLINE half of the restricted-write affordance.
 *
 * `useWriteAccess()` disables the control; this states the reason where the user is actually
 * looking, at the top of the surface they came to edit. A disabled button with only a `title`
 * tooltip is not enough on touch, and the recorded defect was precisely a user who filled in a
 * whole form before learning anything was wrong.
 *
 * RBAC-shaped copy, matching `RestrictedBanner`: HR/Admin get the billing route because they can
 * act on it. Everyone else is told to contact HR and is NOT offered a billing link — a plain
 * employee must not learn a billing surface exists. Same rule the `subscription_restricted` toast
 * in `requestHandling.ts` follows.
 *
 * Renders NOTHING when the tenant is not restricted, so it is safe to mount unconditionally at the
 * top of any write surface.
 */

const BILLING_ROLES = ["Hr", "Admin"];

interface Props {
  /** Defaults to "gated" — the same default as useWriteAccess. */
  area?: WriteArea;
  className?: string;
}

const RestrictedWriteNotice: React.FC<Props> = ({ area = "gated", className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { blocked, reason } = useWriteAccess(area);

  if (!blocked) return null;

  const canManageBilling = BILLING_ROLES.includes(role || "");

  return (
    <div
      role="status"
      data-testid="restricted-write-notice"
      className={`flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 ${className ?? ""}`}
    >
      <Icon name="AlertTriangle" size={18} className="mt-0.5 shrink-0 text-warning" />
      <div className="min-w-0 space-y-1">
        {/*
          🔴 THESE TWO STRINGS WERE ENGLISH ON EVERY LOCALE. `t("Editing is paused")` looks
          translated but is not: with `keySeparator: false` the English sentence WAS the key, and no
          such key existed in any of en/ar/fr, so i18next fell back to echoing the key. An Arabic or
          French tenant read the paywall notice — the one that tells them their data is safe — in a
          language they may not speak, on every paywalled surface in the product. Real keys now, and
          they must stay keys: do not "simplify" these back to literal sentences.
        */}
        <p className="text-sm font-medium text-foreground">{t("restrictedWrite.heading")}</p>
        {/*
          🔴 This element is the ONLY place the reason is rendered as text, and every disabled
          write control on the page points at it with aria-describedby. It must not be moved
          behind a hover, a tooltip or a collapsed section: the disabled controls it explains
          cannot receive hover at all (`disabled:pointer-events-none`), which is exactly how the
          first cut of CO-24 ended up telling the user nothing.
        */}
        <p id={RESTRICTED_WRITE_REASON_ID} className="text-sm text-muted-foreground">{reason}</p>
        {canManageBilling && (
          <button
            type="button"
            onClick={() => navigate("/settings/billing")}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {t("restrictedWrite.goToBilling")}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Compact, VISIBLE inline reason for a control group that sits far from the page-level notice —
 * a floating clocking panel, an action row at the bottom of a long table.
 *
 * It carries no `id`: the page-level `RestrictedWriteNotice` owns `RESTRICTED_WRITE_REASON_ID`,
 * and duplicating an id would break `aria-describedby` for every control on the page. Its value
 * is that the reason is legible *where the dead button is*, without hover and without scrolling.
 * Renders nothing when the tenant is not restricted.
 */
export const RestrictedWriteHint: React.FC<Props> = ({ area = "gated", className }) => {
  const { blocked, reason } = useWriteAccess(area);
  if (!blocked) return null;
  return (
    <p
      data-testid="restricted-write-hint"
      className={`flex items-start gap-1.5 text-xs text-muted-foreground ${className ?? ""}`}
    >
      <Icon name="Lock" size={12} className="mt-0.5 shrink-0 text-warning" />
      <span>{reason}</span>
    </p>
  );
};

export default RestrictedWriteNotice;
